import type { NavSection } from '@lilara/ui-web'

export interface SidebarNavData {
  sections: NavSection[]
  footerSections: NavSection[]
  navReloadMap: Map<string, boolean>
}

export const NAV_ITEM_IDS = {
  bots: 'bots',
  knowledgeBase: 'knowledge-base',
  evaluator: 'evaluator',
  inbox: 'inbox',
  realtimeDashboard: 'realtime-dashboard',
  caseArchive: 'case-archive',
  whatsappCallLogs: 'whatsapp-call-logs',
  contactsList: 'contacts-list',
  infoFields: 'info-fields',
  campaignsV2: 'campaigns-v2',
  campaigns: 'campaigns',
  campaignsContacts: 'campaigns-contacts',
  analyticsOverview: 'analytics-overview',
  analyticsAi: 'analytics-ai',
  analyticsCases: 'analytics-cases',
  analyticsAgents: 'analytics-agents',
  analyticsAutomation: 'analytics-automation',
  analyticsConversations: 'analytics-conversations',
  addOns: 'add-ons',
  settings: 'settings',
  helpCenter: 'help-center',
} as const

export interface SidebarPermissions {
  isAdmin: boolean
  isManager: boolean
  isAgent: boolean
  hasAiKnowledgeAccess: boolean
  hasQaEvaluatorAccess: boolean
  hasWhatsappCallLogsAccess: boolean
  hasCaseArchiveAccess: boolean
  hasContactsAccess: boolean
  showContactsListLink: boolean
  showCampaignsV2Nav: boolean
  hasCampaignsAccess: boolean
  hasAnalyticsAccess: boolean
  hasAnalyticsConversationsAccess: boolean
  hasCopilotAccess: boolean
  hasOrgSettingsAccess: boolean
}

export function buildSidebarNavData(perms: SidebarPermissions): SidebarNavData {
  const navReloadMap = new Map<string, boolean>()
  const sections: NavSection[] = []
  const footerItems: NavSection['items'] = []

  function reload(id: string, value: boolean) {
    navReloadMap.set(id, value)
  }

  // — Automation —
  if (perms.isAdmin) {
    const items: NavSection['items'] = []

    items.push({
      id: NAV_ITEM_IDS.bots,
      label: 'Bots',
      icon: 'message-bot',
      iconPrefix: 'far',
      href: '/automation/bots/all',
    })
    reload(NAV_ITEM_IDS.bots, false)

    if (perms.hasAiKnowledgeAccess) {
      items.push({
        id: NAV_ITEM_IDS.knowledgeBase,
        label: 'Knowledge Base',
        icon: 'book-open',
        iconPrefix: 'far',
        href: '/automation/knowledgebase',
      })
      reload(NAV_ITEM_IDS.knowledgeBase, true)
    }

    sections.push({ label: 'Automation', items })
  }

  // — Evaluator —
  if (perms.hasQaEvaluatorAccess) {
    sections.push({
      label: 'Evaluator',
      items: [
        {
          id: NAV_ITEM_IDS.evaluator,
          label: 'Dashboard',
          icon: 'flask',
          iconPrefix: 'fas',
          href: '/evaluator',
        },
      ],
    })
    reload(NAV_ITEM_IDS.evaluator, false)
  }

  // — Helpdesk —
  {
    const items: NavSection['items'] = [
      {
        id: NAV_ITEM_IDS.inbox,
        label: 'Inbox',
        icon: 'headset',
        iconPrefix: 'far',
        href: '/desk/inbox',
      },
      {
        id: NAV_ITEM_IDS.realtimeDashboard,
        label: 'Realtime Dashboard',
        icon: 'chart-line',
        iconPrefix: 'far',
        href: '/desk/dashboard',
      },
    ]
    reload(NAV_ITEM_IDS.inbox, false)
    reload(NAV_ITEM_IDS.realtimeDashboard, false)

    if (perms.hasCaseArchiveAccess) {
      items.push({
        id: NAV_ITEM_IDS.caseArchive,
        label: 'Case Archive',
        icon: 'box-archive',
        iconPrefix: 'far',
        href: '/desk/archive',
      })
      reload(NAV_ITEM_IDS.caseArchive, false)
    }

    if (perms.hasWhatsappCallLogsAccess) {
      items.push({
        id: NAV_ITEM_IDS.whatsappCallLogs,
        label: 'WhatsApp Call Logs',
        icon: 'headset',
        iconPrefix: 'far',
        href: '/desk/whatsapp-call-logs',
      })
      reload(NAV_ITEM_IDS.whatsappCallLogs, false)
    }

    sections.push({ label: 'Helpdesk', items })
  }

  // — Contacts —
  if (perms.hasContactsAccess) {
    const items: NavSection['items'] = []

    if (perms.showContactsListLink) {
      items.push({
        id: NAV_ITEM_IDS.contactsList,
        label: 'List',
        icon: 'address-card',
        iconPrefix: 'far',
        href: '/contacts/list',
      })
      reload(NAV_ITEM_IDS.contactsList, true)
    }

    items.push({
      id: NAV_ITEM_IDS.infoFields,
      label: 'Info Fields',
      icon: 'list-ul',
      href: '/contacts/info-fields',
    })
    reload(NAV_ITEM_IDS.infoFields, false)

    sections.push({ label: 'Contacts', items })
  }

  // — Campaigns V2 —
  if (perms.showCampaignsV2Nav) {
    sections.push({
      label: 'Campaigns V2',
      items: [
        {
          id: NAV_ITEM_IDS.campaignsV2,
          label: 'Campaigns',
          icon: 'bullhorn',
          iconPrefix: 'far',
          href: '/campaigns-v2/manage',
        },
      ],
    })
    reload(NAV_ITEM_IDS.campaignsV2, true)
  }

  // — Campaigns V1 —
  if (perms.hasCampaignsAccess) {
    const items: NavSection['items'] = [
      {
        id: NAV_ITEM_IDS.campaigns,
        label: 'Campaigns List',
        icon: 'bullhorn',
        iconPrefix: 'far',
        href: '/campaigns/manage',
      },
    ]
    reload(NAV_ITEM_IDS.campaigns, false)

    if (perms.isAdmin) {
      items.push({
        id: NAV_ITEM_IDS.campaignsContacts,
        label: 'Contacts',
        icon: 'address-card',
        iconPrefix: 'far',
        href: '/campaigns/contacts',
      })
      reload(NAV_ITEM_IDS.campaignsContacts, false)
    }

    sections.push({ label: 'Campaigns', items })
  }

  // — Analytics —
  if (perms.hasAnalyticsAccess) {
    const items: NavSection['items'] = [
      {
        id: NAV_ITEM_IDS.analyticsOverview,
        label: 'Overview',
        icon: 'chart-bar',
        iconPrefix: 'far',
        href: '/analytics/overview',
      },
    ]
    reload(NAV_ITEM_IDS.analyticsOverview, false)

    if (perms.hasCopilotAccess) {
      items.push({
        id: NAV_ITEM_IDS.analyticsAi,
        label: 'AI Analytics',
        icon: 'lasso-sparkles',
        iconPrefix: 'fasr',
        href: '/analytics/copilot',
      })
      reload(NAV_ITEM_IDS.analyticsAi, false)
    }

    items.push(
      {
        id: NAV_ITEM_IDS.analyticsCases,
        label: 'Cases',
        icon: 'chart-user',
        iconPrefix: 'far',
        href: '/analytics/cases',
      },
      {
        id: NAV_ITEM_IDS.analyticsAgents,
        label: 'Agents',
        icon: 'chart-user',
        iconPrefix: 'far',
        href: '/analytics/agents',
      },
      {
        id: NAV_ITEM_IDS.analyticsAutomation,
        label: 'Automation Metrics',
        icon: 'bolt-auto',
        iconPrefix: 'far',
        href: '/analytics/automation',
      }
    )
    reload(NAV_ITEM_IDS.analyticsCases, false)
    reload(NAV_ITEM_IDS.analyticsAgents, false)
    reload(NAV_ITEM_IDS.analyticsAutomation, false)

    if (perms.hasAnalyticsConversationsAccess) {
      items.push({
        id: NAV_ITEM_IDS.analyticsConversations,
        label: 'Conversations',
        icon: 'chart-bar',
        iconPrefix: 'far',
        href: '/analytics/conversations',
      })
      reload(NAV_ITEM_IDS.analyticsConversations, false)
    }

    sections.push({ label: 'Analytics', items })
  }

  // — Footer —
  if (!perms.isAgent) {
    footerItems.push({
      id: NAV_ITEM_IDS.addOns,
      label: 'Add ons',
      icon: 'rocket-launch',
      iconPrefix: 'far',
      href: '/addons',
    })
    reload(NAV_ITEM_IDS.addOns, false)
  }

  if (perms.hasOrgSettingsAccess) {
    footerItems.push({
      id: NAV_ITEM_IDS.settings,
      label: 'Settings',
      icon: 'gear',
      iconPrefix: 'far',
      href: '/organization',
    })
    reload(NAV_ITEM_IDS.settings, false)
  }

  footerItems.push({
    id: NAV_ITEM_IDS.helpCenter,
    label: 'Help center',
    icon: 'circle-question',
    iconPrefix: 'fas',
    href: 'https://help.hubtype.com/',
  })
  reload(NAV_ITEM_IDS.helpCenter, true)

  const footerSections: NavSection[] =
    footerItems.length > 0 ? [{ items: footerItems }] : []

  return { sections, footerSections, navReloadMap }
}
